import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import {
  BuildingPickListItemDto,
  BuildingService,
  ProfiNeoSyncStateMergeDto,
  ProfiNeoSyncStateService,
} from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { watch } from '@/utils/watch';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

@Component({
  standalone: false,
  selector: 'wr-profi-neo-sync-dialog',
  templateUrl: './profi-neo-sync-dialog.component.html',
  styleUrls: ['./profi-neo-sync-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfiNeoSyncDialogComponent implements OnInit {

  private readonly dialogRef = inject(NbDialogRef);
  private readonly buildingService = inject(BuildingService);
  private readonly profiNeoSyncStateService = inject(ProfiNeoSyncStateService);
  private readonly toastrService = inject(NbToastrService);

  readonly buildings = signal<BuildingPickListItemDto[]>([]);
  readonly selectedBuildings = signal<Map<string, BuildingPickListItemDto>>(new Map());
  readonly searchText = signal('');
  readonly showCheckedOnly = signal(false);
  readonly loading = signal(false);

  private readonly page = signal(0);
  private readonly resetVersion = signal(0);
  private searchDebounceTimer?: ReturnType<typeof setTimeout>;
  readonly hasMore = signal(true);

  displayedBuildings = computed(() => {
    if (this.showCheckedOnly()) {
      const search = this.searchText().toLowerCase().trim();
      const selected = Array.from(this.selectedBuildings().values());
      if (!search) return selected;
      return selected.filter(b =>
        b.baseData?.sectionText?.toLowerCase().includes(search) ||
        b.internalNumber?.toLowerCase().includes(search) ||
        b.ownerTenantName?.toLowerCase().includes(search),
      );
    }
    return this.buildings();
  });

  selectedCount = computed(() => this.selectedBuildings().size);

  constructor() {
    watch([this.searchText], () => {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        void this.reset();
      }, SEARCH_DEBOUNCE_MS);
    }, { immediate: false });
  }

  async ngOnInit(): Promise<void> {
    await this.loadMore();
  }

  private async reset(): Promise<void> {
    this.resetVersion.update(v => v + 1);
    this.buildings.set([]);
    this.page.set(0);
    this.hasMore.set(true);
    this.loading.set(false);
    await this.loadMore();
  }

  async loadMore(): Promise<void> {
    if (this.loading() || !this.hasMore() || this.showCheckedOnly()) return;

    const versionAtStart = this.resetVersion();
    this.loading.set(true);
    try {
      const currentPage = this.page();
      this.page.update(p => p + 1);
      const more = await firstValueFrom(
        this.buildingService.findAllPickList({
          hasProfiNeoSyncState: false,
          fullTextSearch: this.searchText() || undefined,
          page: currentPage,
          size: PAGE_SIZE,
        }),
      );
      if (this.resetVersion() === versionAtStart) {
        this.buildings.update(current => [...current, ...more]);
        if (more.length < PAGE_SIZE) {
          this.hasMore.set(false);
        }
      }
    } finally {
      this.loading.set(false);
    }
  }

  toggleSelection(building: BuildingPickListItemDto): void {
    const current = new Map(this.selectedBuildings());
    if (current.has(building.id)) {
      current.delete(building.id);
    } else {
      current.set(building.id, building);
    }
    this.selectedBuildings.set(current);
  }

  toggleShowCheckedOnly(): void {
    this.showCheckedOnly.update(v => !v);
  }

  isSelected(id: string): boolean {
    return this.selectedBuildings().has(id);
  }

  async sync(): Promise<void> {
    if (this.selectedBuildings().size === 0) {
      return;
    }

    this.loading.set(true);
    try {
      const mergeDtos: ProfiNeoSyncStateMergeDto[] = Array.from(this.selectedBuildings().keys()).map(buildingId => ({
        buildingId,
      }));
      const startedStates = await firstValueFrom(this.profiNeoSyncStateService.mergeAll(mergeDtos));
      if (startedStates.length < mergeDtos.length) {
        this.toastrService.warning(
          `${startedStates.length} von ${mergeDtos.length} Synchronisierungen gestartet — die übrigen Gebäude haben bereits einen offenen Sync-Auftrag`,
          'Profi.neo',
        );
      } else {
        this.toastrService.success(`${startedStates.length} Synchronisierungen gestartet`, 'Profi.neo');
      }
      this.dialogRef.close(true);
    } catch {
      // ErrorInterceptor already shows a toast; keep the dialog open for retry
    } finally {
      this.loading.set(false);
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
